import { useState } from 'react';

import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/Accordion/Accordion';
import { Badge } from '@/components/Badge/Badge';
import { Button } from '@/components/Button/Button';
import { Checkbox } from '@/components/Checkbox/Checkbox';
import { Combobox } from '@/components/Combobox/Combobox';
import { DatePicker } from '@/components/DatePicker/DatePicker';
import { DateField } from '@/components/DatePicker/DateField';
import { Field } from '@/components/Field/Field';
import { cn } from '@/util/utils';
import { FILTER_COPY } from '@/components/FilterBar/filterCopy';
import {
    type AppliedFilters,
    fieldOptions,
    rangeEnds,
    rangeValue,
    type FilterField,
    type FilterGroup,
    type FilterValue,
    isFilterList,
    groupFilterCount,
    sameFilters,
} from '@/components/FilterBar/filters';
import { Input } from '@/components/Input/Input';
import { Label } from '@/components/Label/Label';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/Select/Select';
import {
    Sheet,
    SheetBody,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    SheetTitles,
} from '@/components/Sheet/Sheet';

export interface FilterDrawerProps<K extends string> {
    /** Mount this component only while it is open; see the note on `pending` below. */
    open: boolean;
    onOpenChange: (open: boolean) => void;
    groups: readonly FilterGroup<K>[];
    /** The set the list is showing; the drawer edits a copy of it. */
    applied: AppliedFilters<K>;
    /** Apply is the only thing in here that changes the list. */
    onApply: (next: AppliedFilters<K>) => void;
    onClearAll: () => void;
    /**
     * What the Apply button says, given what is pending. The wardrobe's drawer names how many
     * pieces the current set would show (3160:2), which is the one thing a reader cannot work
     * out from the drawer itself. Omitted, it reads "Apply filters".
     */
    applyLabel?: (pending: AppliedFilters<K>) => string;
}

/**
 * The design's filter drawer (`.ord-drawer`): the muted uppercase header, then accordion groups
 * that open into a three-column field grid, with Clear all and Apply filters in the footer. A drawer with a single group (lookbooks) draws that group's row as a fixed heading
 * over its fields, which are always open: with nothing else to open, collapsing it would only
 * hide the filters.
 *
 * Pending versus applied is the contract. Everything typed or chosen in here goes into a pending
 * copy and reaches the list only on Apply, so a half-set filter never refilters behind the
 * reader. That also makes the drawer dirty in the `Sheet` sense, so closing it with a pending
 * change asks first instead of silently dropping the set.
 */
export function FilterDrawer<K extends string>({
    open,
    onOpenChange,
    groups,
    applied,
    onApply,
    onClearAll,
    applyLabel,
}: FilterDrawerProps<K>) {
    /*
      Seeded once, from what the list is actually showing. The caller mounts this only while it
      is open, as the user drawer is mounted, so opening it again is a fresh mount and a fresh
      pending copy; that is why there is no effect here syncing the two.
    */
    const [pending, setPending] = useState<AppliedFilters<K>>(applied);
    const [openGroups, setOpenGroups] = useState<string[]>(() =>
        initialOpenGroups(groups, applied),
    );

    const dirty = !sameFilters(groups, pending, applied);
    const only = groups.length === 1 ? groups[0] : undefined;

    function set(key: K, value: FilterValue) {
        setPending((prev) => {
            const next = { ...prev, [key]: value };
            /*
              A field the change has just greyed out goes back to neutral with it. Leaving its
              value would apply a filter the reader can no longer see, reach or remove from here:
              the control is disabled, so the only way back would be the chip in the bar. So does
              a choice the change has narrowed its value out of (ALS-559).
            */
            for (const field of groups.flatMap((group) => group.fields)) {
                if (field.disabledWhen?.(next) || narrowedAway(field, prev, next)) {
                    // Removed rather than set to the neutral option, which is how clearing works
                    // everywhere else here: an absent key is what "not applied" is written as.
                    delete next[field.key];
                }
            }
            return next;
        });
    }

    const fieldsOf = (group: FilterGroup<K>) =>
        group.fields.map((field) => (
            <FilterFieldCell
                key={field.key}
                field={field}
                value={pending[field.key]}
                pending={pending}
                onChange={(value) => set(field.key, value)}
            />
        ));

    return (
        <Sheet open={open} onOpenChange={onOpenChange} dirty={dirty}>
            <SheetContent size="wide" showCloseButton>
                <SheetHeader>
                    <SheetTitles>
                        <SheetTitle className="text-label-lg tracking-[0.06em] uppercase">
                            {FILTER_COPY.drawerTitle}
                        </SheetTitle>
                    </SheetTitles>
                </SheetHeader>

                <SheetBody className="p-0">
                    {only ? (
                        <section aria-labelledby={`filter-group-${only.key}`}>
                            <div className="flex items-center gap-3.5 px-[22px] py-4">
                                <GroupHeading
                                    group={only}
                                    count={groupFilterCount(only, pending)}
                                    titleId={`filter-group-${only.key}`}
                                />
                            </div>
                            <div className={fieldGrid(only)}>{fieldsOf(only)}</div>
                        </section>
                    ) : (
                        <Accordion type="multiple" value={openGroups} onValueChange={setOpenGroups}>
                            {groups.map((group) => {
                                const count = groupFilterCount(group, pending);
                                return (
                                    <AccordionItem
                                        key={group.key}
                                        value={group.key}
                                        className="border-b-0 not-first:border-t"
                                    >
                                        <AccordionTrigger className="hover:bg-muted items-center gap-3.5 rounded-none px-[22px] py-4 hover:no-underline">
                                            <GroupTile group={group} />
                                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                                <span className="text-label-md tracking-[0.05em] uppercase">
                                                    {group.title}
                                                </span>
                                                <span className="text-display-italic text-muted-foreground text-[13px]">
                                                    {group.subtitle}
                                                </span>
                                            </span>
                                            {count > 0 ? (
                                                <Badge variant="secondary" size="sm">
                                                    {count}
                                                </Badge>
                                            ) : null}
                                        </AccordionTrigger>
                                        <AccordionContent className={fieldGrid(group)}>
                                            {fieldsOf(group)}
                                        </AccordionContent>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    )}
                </SheetBody>

                <SheetFooter>
                    <Button variant="ghost" onClick={onClearAll}>
                        {FILTER_COPY.clear}
                    </Button>
                    <span className="ml-auto" />
                    <Button onClick={() => onApply(pending)}>
                        {applyLabel ? applyLabel(pending) : FILTER_COPY.apply}
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
}

/**
 * The groups open when the drawer opens: every one with a filter in force, so the reader lands on
 * what they set rather than hunting for it to change or clear it (ALS-499), and the first group
 * when nothing is set, as before. Read from what the list is showing, once per opening; after
 * that the reader's own opening and closing stands until the drawer closes. Several can be open
 * at once, which is what two filters in two groups needs.
 */
function initialOpenGroups<K extends string>(
    groups: readonly FilterGroup<K>[],
    applied: AppliedFilters<K>,
): string[] {
    const active = groups.filter((group) => groupFilterCount(group, applied) > 0);
    return (active.length > 0 ? active : groups.slice(0, 1)).map((group) => group.key);
}

/**
 * Whether a change has just taken a choice's value out of its list: a secondary category picked
 * under All categories, then a primary that does not hold it (ALS-559). Kept, the select would
 * show nothing and Apply would send a pair that matches no product, with the bare id on its chip.
 * Only a value the list offered before the change counts, so one whose list has not loaded yet
 * (a URL's, while the categories are still on their way) is not dropped by an unrelated edit.
 */
function narrowedAway<K extends string>(
    field: FilterField<K>,
    before: AppliedFilters<K>,
    after: AppliedFilters<K>,
): boolean {
    if (field.kind !== 'choice' || typeof field.options !== 'function') return false;
    const value = after[field.key];
    if (typeof value !== 'string' || value === field.neutral) return false;
    const offered = (set: AppliedFilters<K>) =>
        fieldOptions(field, set).some((option) => option.value === value);
    return offered(before) && !offered(after);
}

/** The field grid under a group's row: one column on a phone, two, then three unless the group asks for two. */
function fieldGrid<K extends string>(group: FilterGroup<K>): string {
    return cn(
        'grid grid-cols-1 gap-x-4 gap-y-3.5 px-[22px] pt-0.5 pb-[22px] sm:grid-cols-2',
        group.columns === 2 ? '' : 'lg:grid-cols-3',
    );
}

/** A group's icon tile. */
function GroupTile<K extends string>({ group }: { group: FilterGroup<K> }) {
    const Icon = group.icon;
    return (
        <span
            data-slot="filter-group-tile"
            className="bg-success/10 text-primary flex size-[38px] flex-none items-center justify-center rounded-md"
        >
            <Icon className="size-5" aria-hidden />
        </span>
    );
}

/** A group's row: the icon tile, the uppercase title over the italic line, and its active count. */
function GroupHeading<K extends string>({
    group,
    count,
    titleId,
}: {
    group: FilterGroup<K>;
    count: number;
    titleId?: string;
}) {
    return (
        <>
            <GroupTile group={group} />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span id={titleId} className="text-label-md tracking-[0.05em] uppercase">
                    {group.title}
                </span>
                <span className="text-display-italic text-muted-foreground text-[13px]">
                    {group.subtitle}
                </span>
            </span>
            {count > 0 ? <Badge variant="secondary">{count}</Badge> : null}
        </>
    );
}

function FilterFieldCell<K extends string>({
    field,
    value,
    pending,
    onChange,
}: {
    field: FilterField<K>;
    value: FilterValue | undefined;
    /** The whole set being edited, for a choice whose options depend on another filter. */
    pending: AppliedFilters<K>;
    onChange: (value: FilterValue) => void;
}) {
    // Greyed by the design where one filter only means anything once another answers a certain
    // way. `set` has already put it back to neutral, so it is showing its own empty state.
    const disabled = field.disabledWhen?.(pending) ?? false;
    /*
      A checkbox list is a list and not a control on a row of them, so it spans the grid rather
      than sitting in one of its three columns. Its own label is the legend of a group, which is
      why it is a fieldset here and a Field everywhere else.
    */
    if (field.kind === 'multi') {
        const chosen = isFilterList(value) ? value : [];
        return (
            <fieldset className="col-span-full flex flex-col gap-2">
                <legend className="text-label-sm text-muted-foreground mb-2">{field.label}</legend>
                <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
                    {field.options.map((option) => {
                        const ticked = chosen.includes(option.value);
                        return (
                            <div
                                key={option.value}
                                className="border-border flex items-center gap-2.5 rounded-md border px-3 py-2.5"
                            >
                                <Checkbox
                                    id={`${field.key}-${option.value}`}
                                    disabled={disabled}
                                    checked={ticked}
                                    onCheckedChange={(next) =>
                                        onChange(
                                            next === true
                                                ? [...chosen, option.value]
                                                : chosen.filter((one) => one !== option.value),
                                        )
                                    }
                                />
                                <Label
                                    htmlFor={`${field.key}-${option.value}`}
                                    className="min-w-0 flex-1 truncate text-sm font-normal"
                                >
                                    {option.label}
                                </Label>
                                <span className="text-label-sm text-muted-foreground shrink-0 tabular-nums">
                                    {field.countLabel(option.count)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </fieldset>
        );
    }

    const text = isFilterList(value) ? undefined : value;

    return (
        <Field label={field.label}>
            {(control) => {
                if (field.kind === 'text' && field.search) {
                    return (
                        <SearchInput
                            {...control}
                            // Plain for the reason the field below is: the register's own box is
                            // the searchbox on this screen.
                            plain
                            placeholder={field.placeholder}
                            disabled={disabled}
                            value={text ?? ''}
                            onChange={(event) => onChange(event.target.value)}
                        />
                    );
                }
                if (field.kind === 'text') {
                    return (
                        <Input
                            {...control}
                            // Deliberately not type="search": the register's own search box is the
                            // searchbox on this screen, and a second role of that name confuses both
                            // a screen reader and a test looking for "the search".
                            type="text"
                            placeholder={field.placeholder}
                            disabled={disabled}
                            value={text ?? ''}
                            onChange={(event) => onChange(event.target.value)}
                        />
                    );
                }
                if (field.kind === 'daterange') {
                    /*
                      One control opening a two-month calendar, which is how the frame draws a
                      From / To (2497:22154) and explicitly not two boxes.
                    */
                    const { from, to } = rangeEnds(text);
                    const start = asDate(from);
                    const end = asDate(to);
                    return (
                        <DatePicker
                            id={control.id}
                            numberOfMonths={2}
                            label={FILTER_COPY.anyDates}
                            className="w-full"
                            disabled={disabled}
                            selected={start || end ? { from: start, to: end } : undefined}
                            onSelect={(picked) =>
                                onChange(rangeValue(asDay(picked?.from), asDay(picked?.to)))
                            }
                        />
                    );
                }
                if (field.kind === 'date') {
                    /*
                      The design's own calendar, not the browser's. `<input type="date">` renders
                      Chrome's picker in the browser's date format, which changes shape per browser
                      and per locale and looks nothing like the frames; Franka raised it on
                      29 Aug 2026 and the prototype's ui-date exists for that reason.
                    */
                    return (
                        <DateField
                            id={control.id}
                            placeholder={FILTER_COPY.anyDate}
                            className="w-full"
                            disabled={disabled}
                            value={text ?? ''}
                            onChange={onChange}
                        />
                    );
                }
                const options = fieldOptions(field, pending);
                if (field.searchable) {
                    // A long list is searched rather than scrolled. The neutral option travels as
                    // "nothing chosen" so clearing the combobox clears the filter.
                    const chosen = text ?? field.neutral;
                    return (
                        <Combobox
                            id={control.id}
                            disabled={disabled}
                            value={chosen === field.neutral ? null : chosen}
                            onValueChange={(next) => onChange(next ?? field.neutral)}
                            options={options
                                .filter((option) => option.value !== field.neutral)
                                .map((option) => ({ value: option.value, label: option.label }))}
                            placeholder={
                                options.find((option) => option.value === field.neutral)?.label ??
                                field.neutral
                            }
                        />
                    );
                }
                const chosen = text ?? field.neutral;
                // Left undefined, SelectValue shows the chosen item's own text.
                const selectedLabel = options.find(
                    (option) => option.value === chosen,
                )?.selectedLabel;
                return (
                    <Select value={chosen} onValueChange={onChange} disabled={disabled}>
                        <SelectTrigger id={control.id}>
                            <SelectValue>
                                {/* Truncated as the item's own text is, so a name still too
                                    long ends in an ellipsis rather than mid-letter. */}
                                {selectedLabel ? (
                                    <span className="truncate">{selectedLabel}</span>
                                ) : undefined}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent align="start">
                            {options.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    <span className="truncate">{option.label}</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                );
            }}
        </Field>
    );
}

/** The ISO day a filter value carries, as the calendar's Date; undefined when it is blank. */
function asDate(day: string | undefined): Date | undefined {
    if (!day) return undefined;
    const date = new Date(`${day}T00:00:00`);
    return Number.isNaN(date.getTime()) ? undefined : date;
}

/**
 * A picked Date as the ISO day the URL carries. Built from the local parts rather than
 * `toISOString`, which converts to UTC and can hand back the day before for anyone east of
 * Greenwich, this team included.
 */
function asDay(date: Date | undefined): string | undefined {
    if (!date) return undefined;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
}
